/**
 * DynamicAttribution — OL control whose attribution list is rebuilt from
 * the visible layers each time visibility changes.
 *
 * Each catalog-discovered layer carries its publisher in the
 * `attributionOrg` layer property (stamped by LegendControl from
 * `pointOfContact` in the ISO record). Visible layers' orgs are
 * deduplicated case- and whitespace-insensitively so two datasets from
 * the same publisher show one entry, regardless of stray spacing or
 * casing differences in the underlying metadata.
 *
 * The basemap attribution (OpenStreetMap + Protomaps) is always shown
 * even when the basemap layer is hidden — the data shown on the map is
 * still derived from those sources, so the attribution requirement
 * still applies.
 */
import Control from 'ol/control/Control';
import { unByKey } from 'ol/Observable';

export default class DynamicAttribution extends Control {
  /**
   * @param {Object} options
   * @param {string} options.alwaysShownHtml - Attribution HTML (already
   *   safe; trusted source) that is rendered on every refresh regardless
   *   of which layers are visible.
   */
  constructor(options) {
    const element = document.createElement('div');
    // Match OL's default attribution markup so existing CSS styles it.
    // `ol-uncollapsible` keeps the panel always visible (no expand
    // button) — the dynamic list is short enough to fit inline.
    element.className = 'ol-attribution ol-unselectable ol-control ol-uncollapsible';
    const list = document.createElement('ul');
    element.appendChild(list);

    super({ element });

    this._list = list;
    this._alwaysShownHtml = options.alwaysShownHtml || '';
    this._layerKeys = new Map();
    this._collectionKeys = [];
  }

  setMap(map) {
    super.setMap(map);
    this._teardown();
    if (!map) return;

    const layers = map.getLayers();
    layers.forEach((l) => this._watchLayer(l));
    this._collectionKeys.push(
      layers.on('add', (e) => { this._watchLayer(e.element); this._render(); }),
      layers.on('remove', (e) => { this._unwatchLayer(e.element); this._render(); })
    );
    this._render();
  }

  _watchLayer(layer) {
    if (this._layerKeys.has(layer)) return;
    const key = layer.on('change:visible', () => this._render());
    this._layerKeys.set(layer, key);
  }

  _unwatchLayer(layer) {
    const key = this._layerKeys.get(layer);
    if (key) {
      unByKey(key);
      this._layerKeys.delete(layer);
    }
  }

  _teardown() {
    for (const key of this._layerKeys.values()) unByKey(key);
    this._layerKeys.clear();
    for (const key of this._collectionKeys) unByKey(key);
    this._collectionKeys = [];
  }

  _render() {
    const map = this.getMap();
    if (!map) return;

    // Case- and whitespace-insensitive dedup. The map preserves the
    // first display form encountered so the original capitalisation is
    // kept; subsequent variants (different casing, extra spaces) are
    // folded silently into the same entry.
    const seen = new Map();
    map.getLayers().forEach((layer) => {
      if (!layer.getVisible()) return;
      const raw = layer.get('attributionOrg');
      if (typeof raw !== 'string') return;
      const display = raw.trim().replace(/\s+/g, ' ');
      if (!display) return;
      const norm = display.toLowerCase();
      if (!seen.has(norm)) seen.set(norm, display);
    });

    const items = [];
    if (this._alwaysShownHtml) {
      items.push('<li>' + this._alwaysShownHtml + '</li>');
    }
    for (const display of seen.values()) {
      items.push('<li>' + _escape(display) + '</li>');
    }
    this._list.innerHTML = items.join('');
  }
}

function _escape(str) {
  const d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}
